import React from 'react';
import { useLocation, useParams } from '@tanstack/react-router';
import { connect, ConnectedProps } from 'react-redux';
import { TFunction } from 'react-i18next';
import { bindActionCreators } from 'redux';

import debounce from 'lodash/debounce';
import {
  StyledContainer,
  Stack,
  List,
  Tooltip,
  Text,
  Pagination,
  TextField,
  withTranslation,
  Information,
  Trans,
} from '@piwikpro/ui-components';
import { ListPreference } from '@piwikpro/user-preferences';
import {
  view,
  withAccess,
  errorBoundary,
  withInject,
  Pagination as PaginationManager,
  ConfigService,
  QueryParams,
  InternalListLink,
  PermissionSelect,
} from '@piwikpro/platform';
import {
  fetchMetaSitePermissionsForUsers as fetchMetaSitePermissionsForUsersMethod,
  MetaSitesState,
  updateMetaSitePermissionsForUser as updateMetaSitePermissionsForUserMethod,
} from '@piwikpro/meta-sites-crate/reducers';
import { DebouncedFunc } from 'lodash';
import { RouterService, buildPaginatedPath } from '@piwikpro/router-crate';
import { permissionSelectOptions } from '../../../../permissionOptions';

const mapStateToProps = (state: MetaSitesState) => ({
  metaSite: state.metaSites.details.settings.settings,
  isLoading: state.metaSites.details.permissions.users.isLoading,
  permissions: state.metaSites.details.permissions.users.permissions,
  total: state.metaSites.details.permissions.users.total,
  failed: state.metaSites.details.permissions.users.failed,
});

const mapDispatchToProps = dispatch => ({
  fetchMetaSitePermissionsForUsers: bindActionCreators(
    fetchMetaSitePermissionsForUsersMethod,
    dispatch,
  ),
  updateMetaSitePermissionsForUser: bindActionCreators(
    updateMetaSitePermissionsForUserMethod,
    dispatch,
  ),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  config: ConfigService
  userPreferences: any
  metaSitePermissionLevels: any
  accessManager: any
  Title: any
  routerService: RouterService
}

interface Props extends ReduxProps, InjectedProps {
  routeParams: QueryParams
  pathname: string
  t: TFunction
}

interface State {
  searchDebounce: null | DebouncedFunc<() => void>
  search: string
}

class Users extends React.Component<Props, State> {
  state = {
    searchDebounce: null,
    search: '',
  }

  constructor(props) {
    super(props);

    this.handleLimitChange = this.handleLimitChange.bind(this);
    this.handlePageChange = this.handlePageChange.bind(this);
    this.renderPermissionsRow = this.renderPermissionsRow.bind(this);
    this.handleUpdatePermissions = this.handleUpdatePermissions.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
  }

  async componentDidMount() {
    await this.fetchPermissions();
  }

  async componentDidUpdate(prevProps: Props) {
    const { routeParams } = this.props;

    if (
      routeParams.page !== prevProps.routeParams.page
      || routeParams.limit !== prevProps.routeParams.limit
    ) {
      await this.fetchPermissions();
    }
  }

  async fetchPermissions() {
    const { fetchMetaSitePermissionsForUsers, metaSite, routeParams } = this.props;
    const { search } = this.state;
    const pagination = PaginationManager.fromQueryParams(
      [],
      0,
      routeParams,
    );

    await fetchMetaSitePermissionsForUsers(metaSite.id, {
      search,
      limit: pagination.getLimit(),
      offset: pagination.offset,
    });
  }

  async handleLimitChange(limit) {
    const {
      routerService,
      pathname,
      userPreferences,
      fetchMetaSitePermissionsForUsers,
      metaSite,
    } = this.props;
    const listPreference = new ListPreference(userPreferences.load(), '');
    const { search } = this.state;

    listPreference.limit = limit;

    userPreferences.persist(listPreference);

    await fetchMetaSitePermissionsForUsers(metaSite.id, {
      search,
      limit,
      offset: 0,
    });

    routerService.push({ to: buildPaginatedPath(pathname, 1, limit) });
  }

  async handlePageChange(offset, pagination) {
    const { search } = this.state;
    const {
      routerService,
      pathname,
      fetchMetaSitePermissionsForUsers,
      metaSite,
    } = this.props;

    await fetchMetaSitePermissionsForUsers(metaSite.id, {
      search,
      limit: pagination.getLimit(),
      offset,
    });

    routerService.push({
      to: buildPaginatedPath(
        pathname,
        pagination.getPageFromOffset(offset),
        pagination.getLimit(),
      ),
    });
  }

  handleSearchChange(event) {
    const { searchDebounce } = this.state;
    const {
      fetchMetaSitePermissionsForUsers,
      metaSite,
      routeParams,
      config,
      routerService,
      pathname,
    } = this.props;
    const pagination = PaginationManager.fromQueryParams(
      [],
      0,
      routeParams,
    );

    if (searchDebounce) {
      (searchDebounce as DebouncedFunc<() => void>).cancel();
    }

    const handleSearch = debounce(() => {
      const { search } = this.state;

      fetchMetaSitePermissionsForUsers(metaSite.id, {
        search,
        offset: pagination.offset,
        limit: pagination.getLimit(),
      });
      routerService.push({ to: buildPaginatedPath(pathname, 1, pagination.getLimit()) });
    }, config.get('LIST_SEARCH_DELAY'));

    this.setState({
      searchDebounce: handleSearch,
      search: event.target.value,
    });

    handleSearch();
  }

  async handleUpdatePermissions(userId, values, { setSubmitting, resetForm }) {
    const { updateMetaSitePermissionsForUser, metaSite } = this.props;

    try {
      await updateMetaSitePermissionsForUser(metaSite.id, userId, values);
    } catch (err) {
      resetForm();
    }

    setSubmitting(false);
  }

  renderPermissionsSelector(user) {
    const {
      accessManager,
      metaSitePermissionLevels,
      t,
    } = this.props;
    const userAccess = user.attributes.permission;
    const isUnableToChange = userAccess.includes('manage') && !accessManager.isGranted({ role: 'OWNER' });
    const SelectControl = () => (
      <PermissionSelect
        initialValue={userAccess}
        options={permissionSelectOptions(metaSitePermissionLevels, t, 'meta-site')}
        // eslint-disable-next-line react/no-this-in-sfc
        onSubmit={(values, api) => (this.handleUpdatePermissions(
          user.id,
          values,
          api,
        ))}
        disabled={isUnableToChange}
      />
    );

    if (isUnableToChange) {
      return (
        <Tooltip content={t('accessControl.permissions.role.owner.required.info')}>
          <div>
            <SelectControl />
          </div>
        </Tooltip>
      );
    }

    return <SelectControl />;
  }

  renderPermissionsRow(user) {
    const {
      metaSitePermissionLevels,
      accessManager,
    } = this.props;
    const userAccess = user.attributes.permission;
    const isOwner = accessManager.isGranted({ role: 'OWNER' });
    const effectivePermission = metaSitePermissionLevels.filter(level => (
      [
        userAccess,
        user.attributes.group_permissions.permission,
      ].includes(level.id)
    ));

    return (
      <List.Item
        key={user.id}
      >
        {isOwner
          ? (
            <InternalListLink
              to="/administration/users/$userId"
              params={{ userId: user.id }}
              label={user.attributes.email}
            />
          )
          : <Text as="p" wrapType="noWrapAndCutText">{user.attributes.email}</Text>}
        {this.renderPermissionsSelector(user)}
        {user.attributes.group_permissions.permission !== 'no-access' ? (
          <Tooltip content={user.attributes.group_permissions.group_names.join(', ')}>
            <div>
              <Text as="p">
                <Trans
                  i18nKey="administration:meta-sites.details.permissions.groupPermission.label"
                  values={{
                    permissionName: metaSitePermissionLevels.find(level => (
                      level.id === user.attributes.group_permissions.permission
                    )).name,
                    permissionGroup: `${user.attributes.group_permissions.group_names.slice(0, 2).join(
                      ', ',
                    )}${user.attributes.group_permissions.group_names.length > 2 ? ', ...' : ''}`,
                  }}
                  components={[
                    <Text weight={600} dataId="TextStyle">permissionName</Text>,
                    <Text weight={600} dataId="TextStyle">permissionGroup</Text>,
                  ]}
                />
              </Text>
            </div>
          </Tooltip>
        ) : null}
        <Text weight={600} dataId="TextStyle">
          {effectivePermission[effectivePermission.length - 1].name}
        </Text>
      </List.Item>
    );
  }

  render() {
    const {
      permissions,
      isLoading,
      failed,
      routeParams,
      t,
      config,
      total,
      Title,
    } = this.props;
    const {
      search,
    } = this.state;
    const pagination = PaginationManager.fromQueryParams(
      permissions,
      total,
      routeParams,
    );

    return (
      <Title part={t('meta-sites.details.title.permissions.users')}>
        {!failed && (
          <Stack.Item fill>
            <Information type="info">{t('meta-sites.details.permissions.info')}</Information>
          </Stack.Item>
        )}
        <Stack.Item fill>
          {!failed && (
            <StyledContainer
              border={['left', 'right', 'top']}
              padding="narrow"
              backgroundColor="gray"
            >
              <TextField
                value={search}
                name="search"
                icon="search"
                onChange={this.handleSearchChange}
                placeholder={t('meta-sites.permissions.users.list.search-placeholder')}
                clearable
              />
            </StyledContainer>
          )}
          <List
            columns={[
              {
                size: 3,
                name: t('meta-sites.details.permissions.column.userName'),
                align: 'left',
              },
              {
                size: 3,
                name: t('meta-sites.details.permissions.column.userPermission'),
                align: 'left',
              },
              {
                size: 3,
                name: t('meta-sites.details.permissions.column.groupPermission'),
                align: 'left',
              },
              {
                size: 3,
                name: t('meta-sites.details.permissions.column.effectivePermission'),
                align: 'left',
              },
            ]}
            isLoading={isLoading}
            error={failed}
          >
            {
              !failed && permissions
                .map(userWithPermissions =>
                  this.renderPermissionsRow(userWithPermissions))}
          </List>
        </Stack.Item>
        {!failed && (
          <Stack.Item fill>
            <Pagination
              onLimitChange={this.handleLimitChange}
              limit={pagination.getLimit()}
              total={total}
              limitOptions={config.get('PAGINATION_ITEMS_PER_PAGE_OPTIONS')}
              startIndex={pagination.offset}
              endIndex={pagination.offset + pagination.getLimit()}
              onPageChange={e => this.handlePageChange(e, pagination)}
            />
          </Stack.Item>
        )}
      </Title>
    );
  }
}

const UsersConnected = connector(withInject<InjectedProps>({
  config: 'config',
  userPreferences: 'userPreferences',
  metaSitePermissionLevels: 'metaSitePermissionLevels',
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
})(
  view({ role: 'USER', level: 'manage' })(withAccess(withTranslation(['administration'])(
    errorBoundary(Users),
  ))),
));

const UsersWrapper = (props) => {
  const routeParams = useParams({
    from: '/_secured/administration/meta-sites/$metaSiteId/permissions/users/$page/$limit',
  });
  const { pathname } = useLocation();

  return (
    <UsersConnected
      {...props}
      routeParams={routeParams}
      pathname={pathname}
    />
  );
};

export default UsersWrapper;
